
import React, { useState } from 'react';
import { Store, MapPin, Wallet, Target, ChefHat, Calendar, Clock, ChevronUp, Trash2, Loader2, MessageCircle, AlertCircle, Timer, History } from 'lucide-react';
import { SystemConfig } from '../../types';
import { AnimatePresence, motion } from 'framer-motion';
import { geocodeAddress } from '../../services/reconService';
import { toast } from 'react-hot-toast';

interface Props {
  config: SystemConfig;
  setConfig: (config: SystemConfig) => void;
}

export const SettingsTabBusiness: React.FC<Props> = ({ config, setConfig }) => {
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [expandedHubDayId, setExpandedHubDayId] = useState<number | null>(null);

  const weekDays = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];

  const formatPhoneMask = (value: string) => {
    const digits = value.replace(/\D/g, '');
    let formatted = digits;
    if (digits.length <= 11) {
      if (digits.length > 2) formatted = `(${digits.substring(0, 2)}) ${digits.substring(2)}`;
      if (digits.length > 7) formatted = `(${digits.substring(0, 2)}) ${digits.substring(2, 7)}-${digits.substring(7)}`;
    }
    return formatted;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digitsOnly = e.target.value.replace(/\D/g, '').substring(0, 11);
    setConfig({ ...config, shopWhatsApp: digitsOnly });
  };

  const handleGeocodeHubAddress = async () => {
    if (!config.businessAddress.trim()) return toast.error("Digite o endereço primeiro.");
    setIsGeocoding(true);
    const toastId = toast.loading("Buscando coordenadas...");
    try {
      const result = await geocodeAddress(config.businessAddress, undefined, undefined, config.googleMapsApiKey);
      if (result && result.lat && result.lng) {
        setConfig({ ...config, businessLat: result.lat, businessLng: result.lng });
        toast.success("Coordenadas atualizadas!", { id: toastId });
      } else {
        toast.error("Endereço não encontrado.", { id: toastId });
      }
    } catch (e) {
      toast.error("Erro na busca.", { id: toastId });
    } finally {
      setIsGeocoding(false);
    }
  };

  const toggleHubDay = (day: number) => {
    const currentHours = config.operatingHours || {};
    const newHours = { ...currentHours };
    if (newHours[day]) {
      delete newHours[day];
      setExpandedHubDayId(null);
    } else {
      newHours[day] = [{ start: "18:00", end: "23:00" }];
      setExpandedHubDayId(day);
    }
    setConfig({ ...config, operatingHours: newHours });
  };

  const addHubRange = (day: number) => {
    const currentHours = config.operatingHours || {};
    if (!currentHours[day]) return;
    const newRanges = [...currentHours[day], { start: "11:00", end: "14:00" }];
    setConfig({ ...config, operatingHours: { ...currentHours, [day]: newRanges } });
  };

  const updateHubRange = (day: number, idx: number, field: 'start' | 'end', val: string) => {
    const currentHours = config.operatingHours || {};
    if (!currentHours[day]) return;
    const newRanges = [...currentHours[day]];
    newRanges[idx] = { ...newRanges[idx], [field]: val };
    setConfig({ ...config, operatingHours: { ...currentHours, [day]: newRanges } });
  };

  const removeHubRange = (day: number, idx: number) => {
    const currentHours = config.operatingHours || {};
    if (!currentHours[day]) return;
    const newRanges = currentHours[day].filter((_, i) => i !== idx);
    if (newRanges.length === 0) {
      const finalHours = { ...currentHours };
      delete finalHours[day];
      setConfig({ ...config, operatingHours: finalHours });
      setExpandedHubDayId(null);
    } else {
      setConfig({ ...config, operatingHours: { ...currentHours, [day]: newRanges } });
    }
  };

  const SectionHeader = ({ icon: Icon, title, subTitle, color = 'text-sal-cyber-teal' }: any) => (
    <div className="flex items-center gap-3 border-b border-border-dim pb-4 mb-6">
      <div className={`p-2.5 bg-card-bg border border-border-dim rounded-xl ${color}`}><Icon className="w-5 h-5" /></div>
      <div><h4 className="font-black text-text-primary uppercase tracking-widest text-sm">{title}</h4><p className="text-xs text-text-muted font-bold uppercase tracking-tighter">{subTitle}</p></div>
    </div>
  );

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-right-4 duration-300 pb-28">
      <div>
        <h3 className="text-3xl font-black text-text-primary mb-1 uppercase tracking-tighter">Configuração de Negócio</h3>
        <p className="text-sm text-text-muted font-medium">Defina as bases operacionais e geográficas do seu Hub.</p>
      </div>

      {Object.keys(config.operatingHours || {}).length === 0 && (
        <div className="bg-status-pending/10 border border-status-pending/20 p-5 rounded-3xl flex gap-4 items-start animate-pulse">
          <AlertCircle className="w-6 h-6 text-status-pending shrink-0" />
          <div>
            <h4 className="text-xs font-black text-status-pending uppercase tracking-widest mb-1">Ação Requerida</h4>
            <p className="text-xs text-text-primary font-bold leading-relaxed uppercase">Seu Hub está fechado para o sistema. Ative pelo menos um dia de funcionamento abaixo para que o Oráculo comece a trabalhar.</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-6">
        <section className="bg-card-bg p-6 rounded-3xl border border-border-dim shadow-xl">
          <SectionHeader icon={MapPin} title="Informações Essenciais do Hub" subTitle="Dados básicos do seu estabelecimento e ponto de partida" color="text-status-ready" />
          <div className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-xs font-black text-text-muted uppercase tracking-widest ml-1">Nome da Loja</label>
                <input type="text" value={config.storeName || ''} onChange={(e) => setConfig({ ...config, storeName: e.target.value })} className="w-full bg-input-bg border border-input-border rounded-2xl px-4 py-3.5 text-sm text-text-primary outline-none focus:border-status-ready/50" placeholder="Ex: Pizzaria S.A.L." />
                <p className="text-xs text-text-muted font-medium mt-1">Nome oficial da sua loja, visível para clientes e em integrações.</p>
              </div>
              <div className="space-y-2">
                <label className="text-xs font-black text-text-muted uppercase tracking-widest ml-1">WhatsApp da Loja</label>
                <input type="text" value={formatPhoneMask(config.shopWhatsApp || '')} onChange={handlePhoneChange} className="w-full bg-input-bg border border-input-border rounded-2xl px-4 py-3.5 text-sm text-status-ready font-bold outline-none focus:border-status-ready/50" placeholder="(82) 99835-0060" />
                <p className="text-xs text-text-muted font-medium mt-1">Número de telefone para contato via WhatsApp com o estabelecimento.</p>
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-xs font-black text-text-muted uppercase tracking-widest ml-1">Endereço Principal do Hub</label>
              <div className="flex gap-2">
                <input type="text" value={config.businessAddress} onChange={(e) => setConfig({ ...config, businessAddress: e.target.value })} className="flex-1 bg-input-bg border border-input-border rounded-2xl px-4 py-3.5 text-sm text-text-primary outline-none" placeholder="Rua, Número, Bairro, Cidade..." />
                <button onClick={handleGeocodeHubAddress} disabled={isGeocoding} className="bg-status-ready text-badge-text-inv px-6 rounded-2xl font-black text-xs uppercase tracking-widest shadow-lg shadow-emerald-900/10">
                  {isGeocoding ? <Loader2 className="w-4 h-4 animate-spin" /> : "Validar"}
                </button>
              </div>
              <p className="text-xs text-text-muted font-medium mt-1">Endereço físico onde seu Hub logístico está localizado. É o ponto de partida central para todas as operações de rota. (<span className="font-bold">Latitude/Longitude</span> geradas automaticamente)</p>
            </div>
          </div>
        </section>
      </div>

      <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-card-bg p-5 rounded-3xl border border-border-dim shadow-lg">
          <div className="flex items-center gap-3 mb-4"><div className="p-2 bg-status-critical/10 text-status-critical rounded-xl border border-status-critical/20"><Target className="w-4 h-4" /></div><span className="text-xs font-black text-text-primary uppercase tracking-widest">Raio Máximo de Entrega</span></div>
          <div className="relative"><input type="number" value={config.maxDeliveryRadiusKm} onChange={(e) => setConfig({ ...config, maxDeliveryRadiusKm: parseInt(e.target.value) || 10 })} className="w-full bg-input-bg border border-input-border text-base font-mono font-black text-text-primary py-3 px-4 rounded-2xl outline-none text-center" /><span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-black text-text-muted">KM</span></div>
          <p className="text-xs text-text-muted font-medium mt-1">Define o raio máximo (em <span className="font-bold">KM</span>) para suas entregas a partir do Hub.</p>
        </div>

        <div className="bg-card-bg p-5 rounded-3xl border border-border-dim shadow-lg">
          <div className="flex items-center gap-3 mb-4"><div className="p-2 bg-status-pending/10 text-status-pending rounded-xl border border-status-pending/20"><ChefHat className="w-4 h-4" /></div><span className="text-xs font-black text-text-primary uppercase tracking-widest">Tempo Médio de Preparo</span></div>
          <div className="relative"><input type="number" value={config.averageKitchenTime} onChange={(e) => setConfig({ ...config, averageKitchenTime: parseInt(e.target.value) || 10 })} className="w-full bg-input-bg border border-input-border text-base font-mono font-black text-text-primary py-3 px-4 rounded-2xl outline-none text-center" /><span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-black text-text-muted">MIN</span></div>
          <p className="text-xs text-text-muted font-medium mt-1">O tempo médio (em <span className="font-bold">minutos</span>) que sua cozinha leva para preparar um pedido.</p>
        </div>

        <div className="bg-card-bg p-5 rounded-3xl border border-border-dim shadow-lg">
          <div className="flex items-center gap-3 mb-4"><div className="p-2 bg-status-critical/10 text-status-critical rounded-xl border border-status-critical/20"><Timer className="w-4 h-4" /></div><span className="text-xs font-black text-text-primary uppercase tracking-widest">Início da Drenagem</span></div>
          <div className="relative"><input type="number" value={config.drainageThresholdMinutes ?? 25} onChange={(e) => setConfig({ ...config, drainageThresholdMinutes: parseInt(e.target.value) || 25 })} className="w-full bg-input-bg border border-input-border text-base font-mono font-black text-text-primary py-3 px-4 rounded-2xl outline-none text-center" /><span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-black text-text-muted">MIN</span></div>
          <p className="text-xs text-text-muted font-medium mt-1">Minutos antes do fechamento para ativar o modo <span className="font-bold text-status-critical">Drenagem</span> (Desativa Retenção).</p>
        </div>

        <div className="bg-card-bg p-5 rounded-3xl border border-border-dim shadow-lg">
          <div className="flex items-center gap-3 mb-4"><div className="p-2 bg-sal-cyber-teal/10 text-sal-cyber-teal rounded-xl border border-sal-cyber-teal/20"><History className="w-4 h-4" /></div><span className="text-xs font-black text-text-primary uppercase tracking-widest">Janela de Inteligência</span></div>
          <div className="relative"><input type="number" value={config.kitchenRollingWindowSize ?? 3} onChange={(e) => setConfig({ ...config, kitchenRollingWindowSize: parseInt(e.target.value) || 3 })} className="w-full bg-input-bg border border-input-border text-base font-mono font-black text-text-primary py-3 px-4 rounded-2xl outline-none text-center" /><span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-black text-text-muted">PEDS</span></div>
          <p className="text-xs text-text-muted font-medium mt-1">Número de pedidos recentes para o cálculo de velocidade da cozinha.</p>
        </div>
      </section>

      <section className="bg-card-bg p-6 rounded-3xl border border-border-dim shadow-xl">
        <SectionHeader icon={Wallet} title="Financeiro da Operação" subTitle="Configurações relacionadas a pagamentos e custos" color="text-status-pending" />

        <div className="flex items-center justify-between p-4 bg-input-bg/30 rounded-2xl border border-border-dim">
          <div>
            <h5 className="text-sm font-bold text-text-primary mb-1">Motoboys Coletam Pagamento?</h5>
            <p className="text-xs text-text-muted leading-tight max-w-[250px]">
              Ative esta opção se seus entregadores estão autorizados a receber dinheiro ou processar pagamentos com cartão.
            </p>
          </div>
          <div
            onClick={() => setConfig({ ...config, driversCollectPayments: !config.driversCollectPayments })}
            className={`w-12 h-7 rounded-full relative cursor-pointer transition-colors ${config.driversCollectPayments ? 'bg-status-pending' : 'bg-border-strong'}`}
          >
            <div className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform ${config.driversCollectPayments ? 'translate-x-5' : 'translate-x-0'}`} />
          </div>
        </div>
      </section>

      <section className="bg-card-bg p-8 rounded-[2.5rem] border border-border-dim shadow-xl relative overflow-hidden">
        <div className="absolute top-0 right-0 p-12 opacity-[0.03] pointer-events-none"><Clock className="w-64 h-64 text-text-primary" /></div>
        <SectionHeader icon={Calendar} title="Horário de Funcionamento" subTitle="Defina os horários operacionais diários" color="text-sal-cyber-teal" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[0, 1, 2, 3, 4, 5, 6].map((day) => {
            const hours = config.operatingHours?.[day];
            const isOpenDay = !!hours;
            const isExpanded = expandedHubDayId === day;
            return (
              <div key={day} className={`relative flex flex-col p-4 rounded-2xl border transition-all duration-500 ${isOpenDay ? 'bg-card-bg border-border-dim shadow-sm' : 'bg-bg-main/50 border-transparent opacity-40 grayscale scale-[0.98]'}`}>
                <div className="flex items-center justify-between gap-3">
                  <button onClick={() => toggleHubDay(day)} className={`w-10 h-10 rounded-xl flex items-center justify-center font-black text-xs transition-all shadow-md ${isOpenDay ? 'bg-sal-cyber-teal text-badge-text-inv scale-105' : 'bg-border-strong text-text-muted'}`}>{weekDays[day].charAt(0)}</button>
                  <div className="flex-1 min-w-0 flex flex-col">
                    <span className={`block text-xs font-black uppercase tracking-widest leading-none mb-1.5 ${isOpenDay ? 'text-text-primary' : 'text-text-muted'}`}>{weekDays[day]}</span>
                    {isOpenDay && hours.length > 0 ? (
                      <div className="flex flex-wrap gap-x-1.5 gap-y-0.5">
                        {hours.map((range, idx) => (
                          <span key={idx} className="text-xs font-mono font-bold text-sal-cyber-teal whitespace-nowrap">
                            {range.start} - {range.end}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-xs font-bold text-text-muted uppercase tracking-tighter">Hub Fechado</span>
                    )}
                  </div>
                  <button onClick={() => setExpandedHubDayId(isExpanded ? null : day)} disabled={!isOpenDay} className={`p-2 rounded-xl transition-all ${isExpanded ? 'bg-sal-cyber-teal/10 text-sal-cyber-teal rotate-180' : 'text-text-muted'}`}><ChevronUp className="w-4 h-4" /></button>
                </div>
                <AnimatePresence>{isExpanded && isOpenDay && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                    <div className="bg-bg-main/30 border border-border-dim rounded-2xl mt-4 p-3 space-y-3">
                      {hours.map((range, idx) => (
                        <div key={idx} className="bg-card-bg p-3 rounded-xl border border-border-dim space-y-2 shadow-sm">
                          <div className="flex items-center justify-between"><span className="text-xs font-black text-text-muted uppercase tracking-widest">Turno {idx + 1}</span>{hours.length > 1 && (<button onClick={() => removeHubRange(day, idx)} className="text-status-critical/50 hover:text-status-critical p-1 rounded-lg transition-all"><Trash2 className="w-3.5 h-3.5" /></button>)}</div>
                          <div className="flex items-center gap-2"><input type="time" value={range.start} onChange={(e) => updateHubRange(day, idx, 'start', e.target.value)} className="flex-1 bg-input-bg border border-input-border rounded-lg px-2 py-1.5 text-xs font-mono font-bold text-center text-text-primary focus:border-sal-cyber-teal/50 outline-none" /><span className="text-text-muted font-bold text-sm">»</span><input type="time" value={range.end} onChange={(e) => updateHubRange(day, idx, 'end', e.target.value)} className="flex-1 bg-input-bg border border-input-border rounded-lg px-2 py-1.5 text-xs font-mono font-bold text-center text-text-primary focus:border-sal-cyber-teal/50 outline-none" /></div>
                        </div>
                      ))}
                      <button onClick={() => addHubRange(day)} className="w-full py-2.5 text-xs font-black text-sal-cyber-teal bg-sal-cyber-teal/5 hover:bg-sal-cyber-teal/10 rounded-xl transition-all uppercase tracking-widest border border-sal-cyber-teal/10">+ Novo Turno</button>
                    </div>
                  </motion.div>
                )}</AnimatePresence>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
};
